/**
 * Backend API client.
 *
 * One place that knows about HTTP, the bearer token and the error envelope, so pages deal in typed data
 * and never in fetch details. The base URL is configuration, not a constant, because the same build runs
 * against a local stack and a deployed one.
 */

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:8080";

export const SIMULATOR_BASE_URL: string =
  (import.meta.env.VITE_SIMULATOR_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const TOKEN_KEY = "talklight.token";
const DEVICE_KEY = "talklight.deviceUuid";

/** The backend's problem envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, detail: string) {
    super(detail);
    this.status = status;
    this.code = code;
  }
}

export function storedToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * The device identity. Generated once per browser profile and persisted, mirroring the Android client:
 * the UUID is the backend's stable device key, and it is the client that owns it.
 */
export function deviceUuid(): string {
  let existing = localStorage.getItem(DEVICE_KEY);
  if (!existing) {
    existing = `web-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_KEY, existing);
  }
  return existing;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = storedToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  const text = await response.text();
  const parsed = text ? JSON.parse(text) : undefined;
  if (!response.ok) {
    const problem = parsed as { code?: string; detail?: string } | undefined;
    throw new ApiError(response.status, problem?.code ?? "error", problem?.detail ?? response.statusText);
  }
  return parsed as T;
}

export const api = {
  get: <T,>(path: string) => request<T>("GET", path),
  post: <T,>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T,>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T,>(path: string) => request<T>("DELETE", path)
};

// --------------------------------------------------------------------------- types

export type NumberState = "ACTIVE" | "DISABLED" | "RELEASED";

export interface Subscription {
  status: NumberState;
  currentTermStart: string | null;
  currentTermEnd: string | null;
  graceExpiresAt: string | null;
  renewalEnabled: boolean;
  paymentFailedAt: string | null;
  providerSubscriptionId: string | null;
}

export interface OwnedNumber {
  id: string;
  e164: string;
  countryCode: string;
  state: NumberState;
  productId: string | null;
  productName: string | null;
  monthlyAmountMinor: number;
  currency: string;
  createdAt: string;
  activatedAt: string | null;
  disabledAt: string | null;
  releasedAt: string | null;
  subscription: Subscription | null;
  devicesRegistered: number;
  devicesOnline: number;
}

export interface Usage {
  period: string;
  calls: number;
  callCap: number;
  messages: number;
  messageCap: number;
}

export interface DeviceView {
  deviceUuid: string;
  displayName: string;
  platform: string;
  online: boolean;
  lastSeenAt: string | null;
  pushCapable: boolean;
}

export interface NumberDetail {
  number: OwnedNumber;
  usage: Usage;
  devices: DeviceView[];
  deviceCap: number;
}

export interface NumberPlan {
  id: string;
  displayName: string;
  countryCode: string;
  phoneNumberType: string;
  currency: string;
  monthlyAmountMinor: number;
  fairUseCalls: number;
  fairUseMessages: number;
}

export interface PurchaseIntent {
  intentId: string;
  status: string;
  productId: string;
  productName: string;
  countryCode: string;
  phoneNumberType: string;
  checkoutUrl: string | null;
  numberId: string | null;
  failureReason: string | null;
  createdAt: string;
}

export interface CommunicationEventView {
  id: string;
  kind: "CALL" | "MESSAGE";
  direction: "INBOUND" | "OUTBOUND";
  numberId: string;
  numberLabel: string | null;
  conversationId: string;
  counterparty: string;
  contactLabel: string;
  occurredAt: string;
  callStatus: string | null;
  callDurationSeconds: number | null;
  messageText: string | null;
  messageStatus: string | null;
  readAt: string | null;
  dismissedAt: string | null;
  blocked: boolean;
}

export interface ConversationView {
  id: string;
  numberId: string;
  numberLabel: string | null;
  counterparty: string;
  contactLabel: string;
  lastEventAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  readUpTo: string | null;
  draftText: string | null;
  draftUpdatedAt: string | null;
}

export interface Thread {
  conversation: ConversationView;
  messages: CommunicationEventView[];
}

export interface Contact {
  id: string;
  e164: string;
  displayName: string;
  note: string | null;
  createdAt: string;
}

export interface BlockedNumber {
  id: string;
  e164: string;
  reason: string | null;
  blockedAt: string;
}

export interface AvailableIntegration {
  id: string;
  name: string;
  endpointUrl: string;
  authScheme: string;
  eventScope: string;
}

export interface IntegrationSubscriptionView {
  integrationId: string;
  integrationName: string;
  numberId: string;
  enabled: boolean;
  tokenSet: boolean;
  authScheme: string;
  eventScope: string;
  updatedAt: string;
}

export interface AdminIntegration extends AvailableIntegration {
  status: string;
  numbersEnabled: number;
  updatedAt: string;
}

export interface ProviderStatus {
  role: string;
  provider: string;
  configured: boolean;
}

export interface CallSessionView {
  callSessionId: string;
  eventId: string;
  numberId: string;
  number: string;
  conversationId: string;
  counterparty: string;
  direction: string;
  state: string;
  answeredDeviceId: string | null;
  ringStartedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  durationSeconds: number | null;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

export interface Session {
  token: string;
  expiresAt: string;
  user: CurrentUser;
}
